
import React from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Router, Cpu, Database, Thermometer, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusIcon, getStatusBadgeClass, getConnectivityIcon, formatUptime } from '../IoTGatewayStatusHelpers';

interface IoTGateway {
  deviceId: string;
  location: string;
  status: string;
  connectedDevices: number;
  uptimeHours: number;
  cpuUsage: number;
  memoryUsage: number;
  temperatureCelsius: number;
  firmwareVersion: string;
  networkStatus: {
    connectivityType: string;
    signalStrength: number;
    dataTransferred: number;
    lastPingTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface IoTGatewayTableRowProps {
  gateway: IoTGateway;
  onSelectDevice: (deviceId: string) => void;
}

const IoTGatewayTableRow: React.FC<IoTGatewayTableRowProps> = ({ gateway, onSelectDevice }) => {
  return (
    <TableRow 
      key={gateway.deviceId}
      className="cursor-pointer hover:bg-muted/80"
      onClick={() => onSelectDevice(gateway.deviceId)}
    >
      <TableCell className="font-medium">{gateway.deviceId}</TableCell>
      <TableCell>{gateway.location}</TableCell>
      <TableCell>
        <div className="flex items-center">
          {getStatusIcon(gateway.status)}
          <Badge className={cn("ml-2 text-xs", getStatusBadgeClass(gateway.status))}>
            {gateway.status}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center">
          <Router className="h-4 w-4 mr-2 text-muted-foreground" />
          {gateway.connectedDevices}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center">
          <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
          {formatUptime(gateway.uptimeHours)}
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center gap-2">
              <div className="flex items-center">
                <Cpu className={cn(
                  "h-4 w-4 mr-1",
                  gateway.cpuUsage > 80 ? "text-red-500" :
                  gateway.cpuUsage > 60 ? "text-amber-500" : "text-green-500"
                )} />
                {gateway.cpuUsage}%
              </div>
              <div className="flex items-center">
                <Database className={cn(
                  "h-4 w-4 mr-1",
                  gateway.memoryUsage > 80 ? "text-red-500" :
                  gateway.memoryUsage > 60 ? "text-amber-500" : "text-green-500"
                )} />
                {gateway.memoryUsage}%
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>CPU Usage: {gateway.cpuUsage}%</p>
              <p>Memory Usage: {gateway.memoryUsage}%</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Thermometer className={cn(
                "h-4 w-4 mr-2",
                gateway.temperatureCelsius > 60 ? "text-red-500" :
                gateway.temperatureCelsius > 45 ? "text-amber-500" : "text-green-500"
              )} />
              {gateway.temperatureCelsius}°C
            </TooltipTrigger>
            <TooltipContent>
              <p>Temperature: {gateway.temperatureCelsius}°C</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              {getConnectivityIcon(gateway.networkStatus.connectivityType)}
              <span className="ml-2">{gateway.networkStatus.connectivityType}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Type: {gateway.networkStatus.connectivityType}</p>
              <p>Signal: {gateway.networkStatus.signalStrength}%</p>
              <p>Data: {gateway.networkStatus.dataTransferred.toFixed(1)} MB</p>
              <p>Ping: {gateway.networkStatus.lastPingTime} ms</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        {gateway.maintenanceStatus.alerts.length > 0 ? (
          <Badge variant="outline" className="text-xs text-red-500">
            {gateway.maintenanceStatus.alerts.length} {gateway.maintenanceStatus.alerts.length === 1 ? 'Alert' : 'Alerts'}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-green-500">
            No Alerts
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
};

export default IoTGatewayTableRow;
